import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { GridColDef } from '@mui/x-data-grid-premium';
import { Table } from '../../components/Table';
import {
  clearReconciliationRunItemDetail,
  fetchReconciliationRunItemDetail,
} from '../../redux/modules/reconciliation/reconciliation.actions';
import {
  reconciliationRunItemDetailLoadingSelector,
  reconciliationSelector,
} from '../../redux/modules/reconciliation/reconciliation.selectors';
import { useAppDispatch } from '../../redux/hooks';
import { ReconciliationItemComparison } from '../../types/reconciliation';

function formatItemStatus(status: string) {
  if (status === 'MATCH') {
    return 'Match';
  }
  if (status === 'MISMATCH') {
    return 'No match';
  }
  return status;
}

export interface ReconciliationItemComparisonViewProps {
  runId: number;
  itemId: number;
  onBack: () => void;
}

export function ReconciliationItemComparisonView({ runId, itemId, onBack }: ReconciliationItemComparisonViewProps) {
  const dispatch = useAppDispatch();
  const itemDetailLoading = useSelector(reconciliationRunItemDetailLoadingSelector);
  const { reconciliationItemDetail, reconciliationItemDetailError } = useSelector(reconciliationSelector);

  useEffect(() => {
    dispatch(fetchReconciliationRunItemDetail({ runId, itemId }));

    return () => {
      dispatch(clearReconciliationRunItemDetail());
    };
  }, [dispatch, runId, itemId]);

  const comparisonColumns = useMemo<GridColDef<ReconciliationItemComparison>[]>(
    () => [
      { field: 'id', headerName: 'ID', width: 72 },
      { field: 'step', headerName: 'Step', width: 72 },
      { field: 'metricName', headerName: 'Metric', flex: 1, minWidth: 220 },
      {
        field: 'isMatch',
        headerName: 'Pass/Fail',
        width: 100,
        sortable: false,
        valueGetter: (p) => (p.row.isMatch ? 'PASS' : 'FAIL'),
      },
      {
        field: 'systemValue',
        headerName: 'System value',
        width: 130,
        valueGetter: (p) => (p.row.systemValue == null ? '' : String(p.row.systemValue)),
      },
      {
        field: 'fileValue',
        headerName: 'File value',
        width: 130,
        valueGetter: (p) => (p.row.fileValue == null ? '' : String(p.row.fileValue)),
      },
      {
        field: 'delta',
        headerName: 'Delta',
        width: 100,
        valueGetter: (p) => (p.row.delta == null || p.row.delta === '' ? '-' : String(p.row.delta)),
      },
      { field: 'message', headerName: 'Message', flex: 1, minWidth: 200, valueGetter: (p) => p.row.message ?? '' },
    ],
    [],
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} variant="outlined" size="small">
          Back to run
        </Button>
      </Box>

      <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
        Item comparison
      </Typography>

      {reconciliationItemDetailError?.message && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {reconciliationItemDetailError.message}
        </Alert>
      )}

      {itemDetailLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!itemDetailLoading && reconciliationItemDetail && (
        <>
          <Stack spacing={0.5} sx={{ mb: 2 }}>
            <Typography variant="body2">Run ID: {runId}</Typography>
            <Typography variant="body2">Item ID: {reconciliationItemDetail.id}</Typography>
            <Typography variant="body2">Order ID: {reconciliationItemDetail.orderId}</Typography>
            <Typography variant="body2">
              Fund: {reconciliationItemDetail.fundName} ({reconciliationItemDetail.fundId})
            </Typography>
            <Typography variant="body2">
              Item status: {formatItemStatus(reconciliationItemDetail.itemStatus)}
            </Typography>
          </Stack>
          <Table
            hideToolbar
            paginationMode="client"
            sortingMode="client"
            filterMode="client"
            wrapperHeight={440}
            rows={reconciliationItemDetail.comparisons}
            columns={comparisonColumns}
            getRowId={(row) => row.id}
            initialPageSize={25}
            pageSizeOptions={[10, 25, 50]}
            loading={false}
            hideFooter={reconciliationItemDetail.comparisons.length <= 10}
          />
        </>
      )}
    </Box>
  );
}
